import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Typography,
} from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import {
  MdArrowBack,
  MdBolt,
  MdCalendarMonth,
  MdVisibility,
  MdFavoriteBorder,
  MdSlideshow,
  MdLogin,
  MdLogout,
  MdHomeWork,
  MdMeetingRoom,
  MdApartment,
} from 'react-icons/md';
import Pagination from 'pages/Pagination';
import { apiGet } from 'utils/apiFetch';
import apiPath from 'utils/apiPath';
import useToastContext from 'hooks/useToastContext';
import {
  BRAND,
  BRAND_HOVER,
  BRAND_SOFT,
  ACTIVITY_LABELS,
  PERIOD_OPTIONS,
} from './constants';
import {
  agentDisplayName,
  avatarColorFromId,
  chartLabel,
  formatExact,
  formatRelative,
  getInitials,
  parsePage,
  parsePeriodDays,
} from './utils';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

const PAGE_SIZE = 10;

function activityIcon(type) {
  if (type === 'login') return MdLogin;
  if (type === 'logout') return MdLogout;
  if (type?.includes('property') && type?.includes('view')) return MdHomeWork;
  if (type?.includes('unit') && type?.includes('view')) return MdMeetingRoom;
  if (type?.includes('project') && type?.includes('view')) return MdApartment;
  if (type?.includes('presentation')) return MdSlideshow;
  if (
    type?.includes('wishlist') ||
    type?.includes('follow') ||
    type?.includes('unfollow')
  ) {
    return MdFavoriteBorder;
  }
  return MdBolt;
}

function AgentDetail() {
  const { agentId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const notification = useToastContext();

  const periodDays = parsePeriodDays(searchParams.get('periodDays'), 14);
  const page = parsePage(searchParams.get('page'), 1);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const setPeriod = (days) => {
    const next = new URLSearchParams(searchParams);
    next.set('periodDays', String(days));
    next.set('page', '1');
    setSearchParams(next);
  };

  const handlePageClick = (event) => {
    const next = new URLSearchParams(searchParams);
    next.set('page', String(event.selected + 1));
    setSearchParams(next);
  };

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/activity-tracker');
    }
  };

  const fetchDetail = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiGet(`${apiPath.activityAgent}/${agentId}`, {
        periodDays,
        page,
        pageSize: PAGE_SIZE,
      });
      if (res?.data?.success) {
        setData(res.data.results);
      } else {
        notification.error(res?.data?.message || 'Unable to load agent activity.');
      }
    } catch (error) {
      notification.error(
        error?.response?.data?.message || 'Unable to load agent activity.'
      );
    } finally {
      setLoading(false);
    }
  }, [agentId, notification, page, periodDays]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const chartData = useMemo(() => {
    const series = data?.series || [];
    return {
      labels: series.map((s, i) => chartLabel(s.date, i, series.length)),
      datasets: [
        {
          label: 'Activities',
          data: series.map((s) => s.count),
          borderColor: BRAND,
          backgroundColor: BRAND_SOFT,
          fill: true,
          tension: 0.35,
          pointRadius: 0,
          pointHoverRadius: 4,
        },
      ],
    };
  }, [data]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false } },
      scales: {
        x: {
          grid: { display: false },
          ticks: { maxRotation: 0, color: '#9CA3AF', font: { size: 11 } },
        },
        y: {
          beginAtZero: true,
          grid: { color: '#F3F4F6' },
          ticks: { color: '#9CA3AF', font: { size: 11 }, precision: 0 },
        },
      },
    }),
    []
  );

  const agent = data?.agent;
  const summary = data?.summary || {};
  const recent = data?.recent || {};
  const breakdownMap = (data?.breakdown || []).reduce((acc, item) => {
    acc[item.category] = item.count;
    return acc;
  }, {});

  const metrics = [
    { label: 'Total activities', value: summary.totalActivities, icon: MdBolt },
    { label: 'Active days', value: summary.activeDays, icon: MdCalendarMonth },
    { label: 'Views', value: summary.views, icon: MdVisibility },
    { label: 'Engagements', value: summary.engagements, icon: MdFavoriteBorder },
    {
      label: 'Presentations',
      value: summary.presentations,
      icon: MdSlideshow,
    },
  ];

  return (
    <div className="px-4 lg:px-7 py-5">
      <Button
        startIcon={<MdArrowBack />}
        onClick={handleBack}
        sx={{
          color: BRAND,
          textTransform: 'none',
          fontWeight: 600,
          mb: 2,
          px: 0,
          '&:hover': { background: 'transparent', color: BRAND_HOVER },
        }}
      >
        Back to activity
      </Button>

      <Card
        variant="outlined"
        sx={{ borderRadius: 2, borderColor: '#E5E7EB', mb: 2.5 }}
      >
        <CardContent
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Avatar
            src={agent?.profilePic || undefined}
            sx={{
              width: 72,
              height: 72,
              fontSize: 24,
              bgcolor: avatarColorFromId(agent?._id || agentId),
            }}
          >
            {getInitials(agent?.firstName, agent?.lastName)}
          </Avatar>
          <div className="flex-1 min-w-[200px]">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <Typography sx={{ fontSize: 22, fontWeight: 700 }}>
                {loading ? '…' : agentDisplayName(agent)}
              </Typography>
              <Chip
                size="small"
                label={agent?.online ? 'Online' : 'Offline'}
                sx={{
                  background: agent?.online ? BRAND_SOFT : '#F3F4F6',
                  color: agent?.online ? BRAND : '#6B7280',
                  fontWeight: 600,
                }}
              />
            </div>
            <Typography sx={{ fontSize: 13, color: '#6B7280' }}>
              Last active ·{' '}
              {agent?.lastActiveAt
                ? formatRelative(agent.lastActiveAt)
                : '—'}
            </Typography>
          </div>
          {agent?.rank != null && (
            <Typography sx={{ color: BRAND, fontWeight: 700, fontSize: 15 }}>
              #{agent.rank} of {agent.totalAgents} agents
            </Typography>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3 mb-4">
        {metrics.map((m) => {
          const Icon = m.icon;
          return (
            <Card
              key={m.label}
              variant="outlined"
              sx={{ borderRadius: 2, borderColor: '#E5E7EB' }}
            >
              <CardContent sx={{ py: '16px !important' }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '8px',
                    background: BRAND_SOFT,
                    color: BRAND,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 1,
                  }}
                >
                  <Icon size={18} />
                </Box>
                <Typography sx={{ fontSize: 28, fontWeight: 700 }}>
                  {loading ? '…' : m.value ?? 0}
                </Typography>
                <Typography sx={{ fontSize: 13, color: '#6B7280' }}>
                  {m.label}
                </Typography>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
        <Card
          variant="outlined"
          sx={{
            borderRadius: 2,
            borderColor: '#E5E7EB',
            gridColumn: { xl: 'span 2' },
          }}
        >
          <CardContent>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <div>
                <Typography sx={{ fontWeight: 700 }}>
                  Activity over time
                </Typography>
                <Typography sx={{ fontSize: 13, color: '#6B7280' }}>
                  Last {periodDays} days
                </Typography>
              </div>
              <div
                className="inline-flex p-1 rounded-lg"
                style={{ background: '#F3F4F6' }}
              >
                {PERIOD_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setPeriod(opt.value)}
                    className="px-3 py-1.5 text-sm rounded-md border-0 cursor-pointer"
                    style={{
                      background:
                        periodDays === opt.value ? '#fff' : 'transparent',
                      color: periodDays === opt.value ? '#111827' : '#6B7280',
                      fontWeight: periodDays === opt.value ? 600 : 400,
                      boxShadow:
                        periodDays === opt.value
                          ? '0 1px 2px rgba(0,0,0,0.06)'
                          : 'none',
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <Box sx={{ height: 240, mb: 2 }}>
              <Line data={chartData} options={chartOptions} />
            </Box>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: 'Property views', value: breakdownMap.views || 0 },
                { label: 'Engagement', value: breakdownMap.engagement || 0 },
                { label: 'Sessions', value: breakdownMap.sessions || 0 },
                {
                  label: 'Presentations',
                  value: breakdownMap.presentations || 0,
                },
              ].map((item) => (
                <div key={item.label}>
                  <Typography sx={{ fontWeight: 700, fontSize: 18 }}>
                    {item.value}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: '#6B7280' }}>
                    {item.label}
                  </Typography>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card
          variant="outlined"
          sx={{ borderRadius: 2, borderColor: '#E5E7EB' }}
        >
          <CardContent>
            <Typography sx={{ fontWeight: 700, mb: 2 }}>
              Recent activity
            </Typography>
            <div className="flex flex-col gap-4">
              {(recent.docs || []).map((row) => {
                const Icon = activityIcon(row.activityType);
                return (
                  <div key={row._id} className="flex gap-3">
                    <span
                      className="inline-flex items-center justify-center w-9 h-9 rounded-lg flex-shrink-0"
                      style={{ background: BRAND_SOFT, color: BRAND }}
                    >
                      <Icon size={16} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                        {row.label ||
                          ACTIVITY_LABELS[row.activityType] ||
                          row.activityType}
                        {row.reference?.label ? (
                          <span className="font-normal text-gray-500">
                            {' '}
                            {row.reference.label}
                          </span>
                        ) : null}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: '#9CA3AF' }}>
                        {formatRelative(row.createdAt)} ·{' '}
                        {formatExact(row.createdAt)}
                      </Typography>
                    </div>
                  </div>
                );
              })}
              {!recent.docs?.length && !loading && (
                <Typography sx={{ fontSize: 13, color: '#9CA3AF' }}>
                  No recent activity.
                </Typography>
              )}
            </div>

            {(recent.totalDocs || 0) > PAGE_SIZE && (
              <Pagination
                page={page}
                handlePageClick={handlePageClick}
                options={{
                  pageCount: recent.totalPages || 0,
                  totalItems: recent.totalDocs || 0,
                  pageRangeDisplayed: 3,
                }}
              />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default AgentDetail;
